/**
 * Crazy Cribbage - Complete Unified Application Bundle
 * Bundles Stylesheets, Configurations, Engines, UI Controllers, and Core Modules into a single deliverable.
 */

// 1. Stylesheets (esbuild extracts this to companion .css)
import '../crazy-cribbage.css';
import '../crazy-cribbage-main.css';
import './ui/card-assassin.css';
import './ui/jail-animation.css';
import './ui/bar-animation.css';
import './ui/cannon-animation.css';
import './ui/speed-trap-animation.css';
import './ui/scenic-route-animation.css';
import './ui/replay-modal.css';
import './ui/room3d-loading-overlay.css';

// 2. Global Game Configurations and Options Engine
import configs from '../game-configs.js';
import optionsEngine from '../game-options-engine.js';

const target = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this);
if (typeof target !== 'undefined') {
  target.GAME_CONFIGS = configs || target.GAME_CONFIGS;
  target.GameOptionsEngine = optionsEngine || target.GameOptionsEngine;
}

// 3. UI Navigation, Brand Detection, and Orbit Menu
import '../crazy-cribbage-main.js';

// 4. Core Modular Classes & Exports
import * as Core from './index.js';
import DOMPurify from 'dompurify';
import { Sanitizer, sanitizeHtml, sanitizeUserInput, initSanitizer, getDOMPurify } from './utils/Sanitizer.js';
import { CrazyCribbageApp } from './app/CrazyCribbageApp.js';
import { CardAssassinOverlay } from './ui/CardAssassinOverlay.js';
import { JailAnimationOverlay } from './ui/JailAnimationOverlay.js';
import { BarAnimationOverlay } from './ui/BarAnimationOverlay.js';
import { CannonAnimationOverlay } from './ui/CannonAnimationOverlay.js';
import { SpeedTrapAnimationOverlay } from './ui/SpeedTrapAnimationOverlay.js';
import { ScenicRouteAnimationOverlay } from './ui/ScenicRouteAnimationOverlay.js';
import { GameReplayManager } from './game/GameReplayManager.js';
import { ReplayModal } from './ui/ReplayModal.js';
import { Room3DLoadingOverlay } from './ui/Room3DLoadingOverlay.js';

if (typeof target !== 'undefined') {
  const activePurify = getDOMPurify() || DOMPurify;
  target.DOMPurify = target.DOMPurify || activePurify;
  target.Sanitizer = Sanitizer;
  target.sanitizeHtml = sanitizeHtml;
  target.sanitizeUserInput = sanitizeUserInput;
  initSanitizer(target);

  target.CrazyCribbage = Object.assign(target.CrazyCribbage || {}, Core);
  target.CrazyCribbageApp = CrazyCribbageApp;
  target.CardAssassinOverlay = CardAssassinOverlay;
  target.showCardAssassin = (options) => CardAssassinOverlay.show(options);
  target.JailAnimationOverlay = JailAnimationOverlay;
  target.showJailAnimation = (options) => JailAnimationOverlay.show(options);
  target.BarAnimationOverlay = BarAnimationOverlay;
  target.showBarAnimation = (options) => BarAnimationOverlay.show(options);
  target.CannonAnimationOverlay = CannonAnimationOverlay;
  target.showCannonAnimation = (options) => CannonAnimationOverlay.show(options);
  target.SpeedTrapAnimationOverlay = SpeedTrapAnimationOverlay;
  target.showSpeedTrapAnimation = (options) => SpeedTrapAnimationOverlay.show(options);
  target.ScenicRouteAnimationOverlay = ScenicRouteAnimationOverlay;
  target.showScenicRouteAnimation = (options) => ScenicRouteAnimationOverlay.show(options);
  target.Room3DLoadingOverlay = Room3DLoadingOverlay;
  target.showRoom3DLoader = (container, options) => Room3DLoadingOverlay.show(container, options);
  target.GameReplayManager = GameReplayManager;
  target.ReplayModal = ReplayModal;
  target.CanadianDoublesModal = Core.CanadianDoublesModal;
  target.showReplaysModal = (brand = 'all') => ReplayModal.openLibrary(brand);
  target.showReplayPlayer = (replay) => ReplayModal.open(replay);
  target.playCurrentGameReplay = () => GameReplayManager.playCurrentGameReplay();
  target.shareCurrentGameReplay = () => GameReplayManager.shareCurrentGameReplay();
  target.shareCurrentReplay = target.shareCurrentGameReplay;
  target.IndexedDBStorage = Core.IndexedDBStorage;
  target.appStorage = Core.appStorage;
  target.AvatarManager = Core.AvatarManager;
  target.PRESET_AVATARS = Core.PRESET_AVATARS;
  target.openAvatarModal = (options) => Core.AvatarManager.openAvatarModal(options);
  target.Profile = Core.Profile;
  target.PlayerProfile = Core.Profile;
  target.getBoardAngles = () => (typeof window !== 'undefined' && window._currentRenderer3D?.getCameraAngles ? window._currentRenderer3D.getCameraAngles() : Core.BoardRenderer3D.getCameraAngles());
  target.getCameraAngles = target.getBoardAngles;
  target.get3DBoardAngles = target.getBoardAngles;
  target.load3DBoardsJson = (source, options) => Core.Board3DCatalog.loadDefaultBoards(source, options);
  target.reload3DBoardsJson = (options) => Core.Board3DCatalog.loadDefaultBoards(null, options);
  target.setTableEnvironment = (env) => (typeof window !== 'undefined' && window._currentRenderer3D?.setTableEnvironment(env));
  target.setPegStyle = (style) => (typeof window !== 'undefined' && window._currentRenderer3D?.setPegStyle(style));
  target.triggerActionCam = (ev) => (typeof window !== 'undefined' && window._currentRenderer3D?.triggerActionCam(ev));
  target.PeggingTrainer = Core.PeggingTrainer;
  target.PEGGING_CATEGORIES = Core.PEGGING_CATEGORIES;
  target.CountingTrainer = Core.CountingTrainer;
  target.CoachPostMortem = Core.CoachPostMortem;
  target.showPeggingTrainerModal = (context) => (typeof window !== 'undefined' ? window.modalManager : null)?.showPeggingTrainerModal?.(context) || Core.ModalManager.showPeggingTrainerModal(context);
  target.showCountingTrainerModal = (context) => (typeof window !== 'undefined' ? window.modalManager : null)?.showCountingTrainerModal?.(context) || Core.ModalManager.showCountingTrainerModal(context);
  target.showCoachPostMortemModal = (context) => (typeof window !== 'undefined' ? window.modalManager : null)?.showCoachPostMortemModal?.(context) || Core.ModalManager.showCoachPostMortemModal(context);
  target.shareCoachPostMortem = (report) => Core.CoachPostMortem.openShareDialog(report);
  target.showGameWonModal = () => Core.GameEngine.showGameWonModal();
  target.TraditionalCard = Core.TraditionalCard;

  // Instantiate application and expose all methods onto window for inline onclick handlers
  const app = new CrazyCribbageApp();
  app.exposeGlobals(target);
  target.crazyCribbageApp = app;

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => app.init());
    } else {
      app.init();
    }
  }
}

// Re-export core classes
export * from './index.js';
